import { useState, useEffect } from 'react';
import { subscriptionApi, Plan, CurrentSubDto } from '../../api/subscriptionApi';
import PlansModal from '../../components/common/PlansModal';
import SettingsWrapper from './SettingsWrapper';
import toast from 'react-hot-toast';

const COLOR_CONFIG: Record<string, { border: string; badge: string }> = {
    gray:   { border: 'border-gray-200',  badge: 'bg-gray-100 text-gray-700' },
    teal:   { border: 'border-teal-300',  badge: 'bg-teal-50 text-teal-700' },
    purple: { border: 'border-purple-300', badge: 'bg-purple-50 text-purple-700' },
};

const SubscriptionPage = () => {
    const [sub, setSub] = useState<CurrentSubDto | null>(null);
    const [plans, setPlans] = useState<Plan[]>([]);
    const [loading, setLoading] = useState(true);
    const [plansOpen, setPlansOpen] = useState(false);
    const [showCancel, setShowCancel] = useState(false);
    const [cancelling, setCancelling] = useState(false);

    const load = async () => {
        setLoading(true);
        const [s, p] = await Promise.all([subscriptionApi.getCurrentSubscription(), subscriptionApi.getPlans()]);
        setSub(s); setPlans(p); setLoading(false);
    };

    useEffect(() => { load(); }, []);

    const handleCancel = async () => {
        setCancelling(true);
        try {
            const res = await subscriptionApi.cancelSubscription();
            if (res.success) { toast.success('Cancelled'); setShowCancel(false); await load(); }
            else toast.error(res.message || 'Failed');
        } catch { toast.error('Error'); } finally { setCancelling(false); }
    };

    const currentPlan = plans.find(p => p.name === sub?.planName);

    if (loading) return (
        <SettingsWrapper title="Subscription" subtitle="Manage your plan">
            <div className="h-40 bg-gray-100 rounded-xl animate-pulse"/>
        </SettingsWrapper>
    );

    return (
        <SettingsWrapper title="Subscription" subtitle="Manage your plan">
            {sub ? (
                <div className={`bg-white border-2 rounded-xl p-5 mb-4 shadow-sm ${COLOR_CONFIG[currentPlan?.color || 'gray'].border}`}>
                    <div className="flex items-start justify-between mb-4">
                        <div>
                            <div className="flex items-center gap-2">
                                <h3 className="text-base font-bold text-gray-900">{sub.planName} Plan</h3>
                                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${sub.isExpired ? 'bg-red-50 text-red-600' : 'bg-green-50 text-green-700'}`}>
                                    {sub.isExpired ? 'Expired' : 'Active'}
                                </span>
                            </div>
                            <p className="text-xs text-gray-400 mt-0.5">
                                {sub.isExpired ? 'Expired' : `Until ${new Date(sub.endDate).toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' })}`}
                            </p>
                        </div>
                        <span className="text-xl font-black text-gray-900">{currentPlan?.isFree ? 'Free' : `$${currentPlan?.price}/mo`}</span>
                    </div>
                    {sub.searchLimit !== -1 && (
                        <>
                            <div className="flex justify-between text-xs mb-1.5">
                                <span className="text-gray-500">Searches used</span>
                                <span className="font-bold text-gray-700 font-mono">{sub.searchesUsed} / {sub.searchLimit.toLocaleString()}</span>
                            </div>
                            <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                                <div className={`h-full rounded-full ${(sub.searchesUsed / sub.searchLimit) >= 0.9 ? 'bg-red-500' : 'bg-teal-500'}`}
                                    style={{ width: `${Math.min(100, (sub.searchesUsed / sub.searchLimit) * 100)}%` }}/>
                            </div>
                        </>
                    )}
                    <div className="flex gap-2 mt-4">
                        <button onClick={() => setPlansOpen(true)} className="flex-1 py-2 bg-teal-600 hover:bg-teal-700 text-white text-sm font-bold rounded-xl">
                            {sub.isExpired ? 'Renew' : 'Upgrade'}
                        </button>
                        {!currentPlan?.isFree && !sub.isExpired && (
                            <button onClick={() => setShowCancel(true)} className="px-4 py-2 border border-gray-200 text-gray-600 text-sm font-semibold rounded-xl hover:bg-red-50 hover:text-red-600 transition-all">Cancel</button>
                        )}
                    </div>
                </div>
            ) : (
                <div className="bg-amber-50 border border-amber-100 rounded-xl p-4 mb-4 flex items-center justify-between">
                    <p className="text-sm font-bold text-amber-700">No active plan</p>
                    <button onClick={() => setPlansOpen(true)} className="px-4 py-2 bg-teal-600 text-white text-sm font-bold rounded-xl">View Plans</button>
                </div>
            )}

            {/* Plans grid */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {plans.map(plan => {
                    const isCurrent = plan.name === sub?.planName && !sub?.isExpired;
                    const c = COLOR_CONFIG[plan.color] || COLOR_CONFIG.gray;
                    return (
                        <div key={plan.id} className={`border-2 rounded-xl p-4 bg-white transition-all ${isCurrent ? c.border : 'border-gray-100'}`}>
                            <div className="flex items-center justify-between mb-2">
                                <span className="text-sm font-bold text-gray-900">{plan.name}</span>
                                {isCurrent && <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${c.badge}`}>Current</span>}
                            </div>
                            <p className="text-xl font-black text-gray-900 mb-1">
                                {plan.isFree ? 'Free' : `$${plan.price}`}
                                {!plan.isFree && <span className="text-xs font-normal text-gray-400">/mo</span>}
                            </p>
                            <p className="text-[11px] text-gray-500">{plan.searchLimit === -1 ? 'Unlimited' : `${plan.searchLimit.toLocaleString()} searches`}{plan.isFree ? '/day' : '/month'}</p>
                            {!isCurrent && (
                                <button onClick={() => setPlansOpen(true)} className="w-full mt-3 py-1.5 text-[12px] font-bold text-teal-600 border border-teal-200 rounded-lg hover:bg-teal-50 transition-all">
                                    {plan.isFree ? 'Downgrade' : 'Upgrade'}
                                </button>
                            )}
                        </div>
                    );
                })}
            </div>

            {showCancel && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm px-4">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 text-center">
                        <h3 className="text-base font-bold text-gray-900 mb-2">Cancel Subscription?</h3>
                        <p className="text-sm text-gray-500 mb-6">You will lose access immediately.</p>
                        <div className="flex gap-3">
                            <button onClick={() => setShowCancel(false)} className="flex-1 py-2.5 border border-gray-200 text-gray-600 text-sm font-semibold rounded-xl">Keep Plan</button>
                            <button onClick={handleCancel} disabled={cancelling} className="flex-1 py-2.5 bg-red-500 text-white text-sm font-bold rounded-xl disabled:opacity-60 flex items-center justify-center gap-2">
                                {cancelling && <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"/>} Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}
            <PlansModal isOpen={plansOpen} onClose={() => { setPlansOpen(false); load(); }}/>
        </SettingsWrapper>
    );
};

export default SubscriptionPage;